import { AuditDetails, Entity, EntityLink, ItemLinkEntity } from '../entity';
import { AccountingResult } from '../accounting/accounting.result';
import { TaxInclusiveOption, PurchaseOrderLine } from '../purchase/purchase.order.line';
import { Attachment } from '../shared/attachment';
import { Address } from '../shared/address';
import { AdjustmentType, CodeStatus, CodeInvoiceStatus } from '../shared/code';
import { CodeUoM } from '../shared/uom';
import { CodeType } from '../shared/code.type';
import { TaxRate } from '../taxrate/tax.rate';
import { WorkOrder } from '../job/workorder';
import { Job } from '../job/job';
import { Company } from '../tenant/company';
import { InvoiceWebHookSource } from '../webhooks/invoice.web.hook.source';
import { Contact } from '../contact/contact';
import { InvoiceContact } from './invoicing.contact';
import { InvoiceStatus } from './invoicing.enum';
import { Payment } from '../payment/payment';
import { EntitySubStatus } from '../entity.status/entity.status';

/**
 * Line item validation issue types.
 * Backend reports facts only - frontend decides severity/exclusion policy.
 */
export type ValidationIssueType = 'code_not_found' | 'code_not_detected' | 'code_inactive' | 'price_mismatch' | 'price_missing' | 'price_book_missing';

/**
 * Validation results for line items.
 * Maps lineId -> array of issue types for that line.
 * Empty array or missing key = no issues.
 */
export type LineValidationResults = Record<string, ValidationIssueType[]>;


export enum InvoiceDateOption {
    CurrentDate = 'Current Date',
    CompletedDate = 'Completed Date',
    FinalisedDate = 'Finalised Date'
}


export enum InvoiceProcessingType {
    Draft = 'Draft',
    Approved = 'Approved',
    ApprovedAndSend = 'ApprovedAndSend',
    Void = 'Void',
    Error = 'Error',
    Reissue = 'Reissue',
    Sync = 'Sync',
    UpdateAndRecalculate = 'UpdateAndRecalculate'
}

export enum InvoiceDisplayMode {
    Job = 'Job',
    Global = 'Global',
    WorkOrder = 'WorkOrder'
}

export enum InvoiceType {
    Invoice = 'Invoice',

    PrepaymentInvoice = 'Prepayment Invoice',
    PrepaymentSupplier = 'Prepayment Supplier',

    CreditNote = 'Credit Note',
    CreditNoteSupplier = 'Credit Note Supplier',

    Bill = 'Bill',
    SupplierBill = 'Supplier Bill',
}


export type InvoiceBuildType = 'Manual' | 'Job' | 'Stage';
export type BillBuildType = 'Manual' | 'SuppliedByContractor' | 'NotSuppliedByContractor' | 'PrepaymentSupplier' | 'PurchaseOrder';

export interface InvoiceLine {
    _id: string;
    name: string;
    description: string;
    quantity: number;
    price: number;
    /** Line discount — Positive amount or percent; type in discountType. */
    discount?: number;
    /** Matches quote AdjustmentType: Value ($) or Percent (%). Default Value. */
    discountType?: AdjustmentType;
    tax: number;
    subTotal: number;
    total: number;
    location?: string;
    notes?: string;
    codeStatus?: CodeStatus;
    uom?: string;
    codeUoM?: CodeUoM;
    catalogueCodeId?: string;
    canOverrideDesc?: boolean;
    itemLink?: ItemLinkEntity;
    links?: ItemLinkEntity[];

    glCode?: string;
    taxRate?: TaxRate;
    type?: CodeType;
    codeInvoicingStatus?: CodeInvoiceStatus;
}

export interface InvoiceBillingCompany extends Entity {
    address: Address;
    phone: string;
    email: string;
    taxRate: number;
    taxId: string;
    invoiceDetails?: string;
}

export interface InvoicePatch {
    active: boolean;
    dueDate: Date;
    generatedDate?: Date;
    lastIssuedDate?: Date;
    customerReference: string;
    notes: string;
    status: InvoiceStatus;
    integration?: AccountingResult;
    lines: InvoiceLine[];
    taxInclusiveOption: TaxInclusiveOption;
    taxRate: TaxRate;
    attachments?: Attachment[];
    terms: string;
    subTotal?: number;
    total?: number;
    tax?: number;
    timezone?: string;
    jobAddress?: Address;
    modifiedAfterSend?: boolean;
    source?: InvoiceWebHookSource;

    amountPaid?: number;
    amountRefunded?: number;

    lastPaymentDate?: Date;
    roundingAmount?: number;
    isReconcile?: boolean;
    outstandingAmount?: number;
    creditAmount?: number;

    systemTags?: Entity[];
}

export interface InvoiceBase extends AuditDetails {
    _id: string;
    invoiceNumber: string;
    invoiceDate: Date;
    contact: InvoiceContact;
    billingCompany: InvoiceBillingCompany;
    job: Entity;
    workOrder?: Entity;
    version?: number;
    resources?: InvoiceContact[];
    timesheetIds?: string[];
    pricebook?: Entity;
    theme?: Entity;

    type?: InvoiceType;

    // no save to db, should using difference interface for display purpose
    hasPayments?: boolean;
    hasAllocation?: boolean;
    payments?: Payment[];
    links?: EntityLink[];

    pdf?: string;
}

export interface PrepaymentInvoicePatch {
    prepaymentSubTotal?: number;
    prepaymentTotal?: number;
    prepaymentTax?: number;
}

export interface CopiedFromSource {
    sourceId: string;
    sourceType: any;
    sourceNumber: string;
    destinationType: any;
}

export interface Invoice extends InvoiceBase, InvoicePatch, PrepaymentInvoicePatch {
    isBulkInvoice?: boolean;
    copiedFromSource?: CopiedFromSource;
    // Optional tenant-configurable refinement of the system status (EntityStatus framework). The
    // InvoiceStatus enum remains the system authority; a sub-status only adds meaning within it
    // (e.g. a Draft consolidated invoice awaiting upstream approval).
    subStatus?: EntitySubStatus;
    // This invoice's id in the UPSTREAM source system (platform convention — same semantics as
    // Job.integrationId / Allocation.integrationId; e.g. the HomesVIC invoice reference number,
    // assigned when the invoice is submitted upstream). NOT the accounting-package sync — that is
    // the separate `integration: AccountingResult`, which the accounting pipeline overwrites on
    // approval and must never carry upstream references.
    integrationId?: string;
}

export interface BillCreatorContext {
    aiProcessInboxId?: string;
    aiProcessCreatedAt?: Date;
    validationResults?: LineValidationResults;
    workOrder?: Omit<WorkOrder, 'claimCodes'>;
    accountValidation?: {
        code: 'BILL_TO_DIFFERENT_ACCOUNT' | 'CONTRACTOR_NOT_STAGE_ASSIGNED' | 'NO_STAGE_CONTRACTOR';
        severity: 'info' | 'error';
    };
    // Sum of AI-extracted line totals (tax-inclusive) before any system rule re-derivation.
    // Used by the bill creator to surface a mismatch warning when the recalculated total
    // diverges from the scanned document total.
    scannedTotal?: number;
}

export interface Bill extends InvoiceBase, InvoicePatch, PrepaymentInvoicePatch {
    copiedFromSource?: CopiedFromSource;
    creatorContext?: BillCreatorContext;
}


export interface QuotePrepaymentInvoiceSummary extends Pick<Invoice,
    '_id' |
    'invoiceNumber' |
    'invoiceDate' |
    'prepaymentTotal' |
    'amountPaid'
> {
}


export interface InvoiceDownload extends Invoice {
    job: Job;
    customer: Contact;
    company: Company;
}

export interface BillDownload extends Bill {
    workOrder: WorkOrder;
    job: Job;
    technician: Contact;
    company: Company;
}

export interface InvoiceBaseEntity extends Entity {
    jobNumber: string;
    jobId: string;
    workOrderNumber: string;
    workOrderId: string;
}

export interface InvoicingSummary extends Pick<
    Invoice,
    '_id' | 'status' | 'invoiceNumber' | 'invoiceDate' |
    'dueDate' | 'total' | 'subTotal' | 'version' |
    'jobAddress' | 'timezone' | 'amountPaid' | 'outstandingAmount' |
    'type' | 'prepaymentSubTotal' | 'prepaymentTotal' | 'creditAmount' | 'lastPaymentDate' |
    'roundingAmount' | 'isReconcile'
> {
    integration?: { success: boolean, type: string, integrationInvoiceId: string };

    billingCompany?: string;
    job?: string;
    workOrder?: string;

    contact: string;
    contactId?: string;
    jobId?: string;
    workOrderId?: string;
    company?: Company;
    address: Address;

}

export interface LineAccounting {
    lines: PurchaseOrderLine[] | InvoiceLine[];
    subTotal: number;
    tax: number;
    total: number;
    supplierMarkup?: number;
}


export interface InvoiceLinePriceRequest {
    buyPriceId?: string;
    sellPriceId?: string;

    line: InvoiceLine;
}
